// ── ImagePicker ─────────────────────────────────────────────────────────────────
// One control for every photo field: upload from the device (phone camera roll included, via the
// file input) or paste a URL. Used for group covers, company logos, social posts and shop listings so
// they all behave the same way and share one size ceiling.
import React, { useRef, useState } from 'react';
import { fileToStoredImage, isSafeImageValue } from '../utils/imageUpload';

let idSeq = 0;

export default function ImagePicker({
  value,                    // current data URL or https URL ('' = none)
  onChange,                 // (nextValue: string) => void
  preset = 'cover',         // see IMAGE_PRESETS
  label = 'Photo',
  hint = '',
  allowUrl = true,          // also offer a paste-a-link field
  previewHeight = 80,
  previewWidth = 80,
  round = false,
  disabled = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef(null);
  const inputId = useRef(`img-pick-${++idSeq}`).current;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Reset immediately so choosing the same file twice still fires a change event.
    e.target.value = '';
    if (!file) return;
    setError(''); setBusy(true);
    try {
      onChange(await fileToStoredImage(file, preset));
    } catch (err) {
      setError(err.message || 'Could not process that image');
    } finally { setBusy(false); }
  };

  const isUpload = /^data:/i.test(value || '');

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{
          width: previewWidth, height: previewHeight, borderRadius: round ? '50%' : 12,
          border: '2px dashed var(--border)', background: 'var(--surface2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          {value
            ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                   onError={() => setError('That link could not be loaded as an image')} />
            : <span style={{ fontSize: 26, opacity: 0.5 }}>🖼️</span>}
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <label htmlFor={inputId}
            style={{
              display: 'inline-block', padding: '8px 16px', borderRadius: 8,
              border: '1.5px solid var(--primary)', color: 'var(--primary)',
              background: 'var(--primary-light)', fontWeight: 600, fontSize: 'var(--font-sm)',
              cursor: disabled ? 'not-allowed' : busy ? 'wait' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}>
            {busy ? '⏳ Processing…' : value ? '🔄 Change photo' : '📷 Upload photo'}
          </label>
          {/* `accept="image/*"` is what makes a phone offer the camera and camera roll. */}
          <input id={inputId} ref={inputRef} type="file" accept="image/*"
            onChange={pick} disabled={disabled || busy} style={{ display: 'none' }} />

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {value && (
              <button type="button" onClick={() => { onChange(''); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit' }}>
                Remove
              </button>
            )}
            {allowUrl && (
              <button type="button" onClick={() => setShowUrl(s => !s)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit' }}>
                {showUrl ? 'Hide link field' : 'or paste a link'}
              </button>
            )}
          </div>

          {allowUrl && showUrl && (
            <input className="input" style={{ marginTop: 8 }}
              placeholder="https://…/photo.jpg"
              value={isUpload ? '' : (value || '')}
              onChange={e => {
                const v = e.target.value;
                setError(isSafeImageValue(v) ? '' : 'Links must start with http:// or https://');
                onChange(v);
              }} />
          )}

          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
            {hint || 'JPG or PNG up to 10 MB — resized automatically.'}
          </p>
          {isUpload && <p style={{ fontSize: 11, color: 'var(--success)', marginTop: 2 }}>✓ Photo uploaded</p>}
          {error && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}
